<template>
  <div class="recycle-bin-tools-bar d-flex">
    <!-- Restore files menu -->
    <div class="mr-1" style="flex-grow: 3;" v-if="$canControlNodeAndPod()">
      <v-menu v-model="showingRestoreMenu">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            class="bg-primary"
            variant="flat"
            block
            :disabled="!hasAnyItems"
            prepend-icon="mdi-file-restore-outline"
            append-icon="mdi-menu-down"
          >
            <span v-if="$vuetify.display.mdAndUp">
              {{ $t('routes.recycleBin.restoreDropdownLabel') }}
            </span>
          </v-btn>
        </template>

        <v-list>
          <!-- Restore all -->
          <v-list-item @click="restoreAll" >
            <v-list-item-title>
              {{ $t('routes.recycleBin.restoreAll') }}
            </v-list-item-title>
          </v-list-item>

          <!-- Restore selected -->
          <v-list-item
            @click="restoreSelected"
            :disabled="!hasSelectedItems"
          >
            <v-list-item-title>
              {{ $t('routes.recycleBin.restoreSelected') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Delete files menu -->
    <div class="mr-1" style="flex-grow: 3;" v-if="$canDeleteNodeAndPod()">
      <v-menu v-model="showingPermanentlyDeleteMenu">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            class="bg-error"
            variant="flat"
            block
            :disabled="!hasAnyItems"
            prepend-icon="mdi-delete-forever-outline"
            append-icon="mdi-menu-down"
          >
            <span v-if="$vuetify.display.mdAndUp">
              {{ $t('routes.recycleBin.deleteDropdownLabel') }}
            </span>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-if="$isTeamUser()" @click="openDeleteAllDialog()">
            <v-list-item-title>
              {{ $t('routes.recycleBin.permanentlyDeleteAll') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item :disabled="!hasSelectedItems" @click="openDeleteSelectedDialog()">
            <v-list-item-title>
              {{ $t('routes.recycleBin.permanentlyDeleteSelected') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Search form -->
    <div class="mr-1 pt-0" style="flex-grow: 2;">
      <v-text-field
        v-model="searchText"
        @update:model-value="searchByFileName"
        id="search-box-input"
        prepend-inner-icon="mdi-magnify"
        :label="$t('routes.recycleBin.findFilesAndFolders')"
        hide-details
        variant="outlined"
        density="comfortable"
        clearable
      />
    </div>

    <!-- Filter menu -->
    <div class="mr-1">
      <v-menu
        v-model="showingFilterMenu"
        :close-on-content-click="false"
        :width="380"
        location="end bottom"
        origin="end top"
        transition="scale-transition"
        :offset="8"
      >
        <template v-slot:activator="{ props: activatorProps }">
          <v-tooltip location="bottom" :aria-label="$t('routes.recycleBin.filters')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="{ ...props, ...activatorProps }"
                variant="text"
                :size="$vuetify.display.smAndDown ? 'small' : undefined"
                :color="hasActiveFilters ? 'primary' : undefined"
                :aria-label="$t('routes.recycleBin.filters')"
              >
                <v-icon>mdi-filter-variant</v-icon>
                <v-badge
                  v-if="hasActiveFilters"
                  color="primary"
                  dot
                  :offset-x="-5"
                  :offset-y="-5"
                />
              </v-btn>
            </template>
            <span>{{ $t('routes.recycleBin.filters') }}</span>
          </v-tooltip>

        </template>

        <v-card elevation="2" border>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h4 class="text-h6">{{ $t('routes.recycleBin.filters') }}</h4>
              </v-col>
            </v-row>

            <v-container fluid class="ma-0 pa-0">
              <!-- Deletion type filter -->
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="selectedFilter"
                    :items="filterOptions"
                    item-title="label"
                    item-value="value"
                    :label="$t('routes.recycleBin.deletionType', 'Deletion type')"
                    variant="solo-filled"
                    density="compact"
                    hide-details
                    flat
                    single-line
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Future filters can be added here -->
            </v-container>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-btn
              color="primary"
              variant="flat"
              size="x-small"
              @click="applyFilters"
            >
              {{ $t('common.apply', 'Apply') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              variant="text"
              size="x-small"
              @click="clearFilters"
              :disabled="!hasActiveFilters"
            >
              {{ $t('common.clear', 'Clear') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </div>

    <!-- View type toggle button -->
    <div class="mr-1">
      <v-tooltip
        location="bottom"
        :aria-label="viewType === 'list' ? $t('menus.topBars.viewAsGrid') : $t('menus.topBars.viewAsList')"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            :size="$vuetify.display.smAndDown ? 'small' : undefined"
            @click="$emit('toggleView')"
            elevation="0"
            color="transparent"
            class="mr-1"
            :aria-label="viewType === 'list' ? $t('menus.topBars.viewAsGrid') : $t('menus.topBars.viewAsList')"
            tile
          >
            <v-icon v-if="viewType === 'list'" class="mr-0" size="20px">mdi-view-module</v-icon>
            <v-icon v-else class="mr-0" size="20px">mdi-view-list</v-icon>
          </v-btn>
        </template>
        <span v-if="viewType === 'list'">{{ $t('menus.topBars.viewAsGrid') }}</span>
        <span v-else>{{ $t('menus.topBars.viewAsList') }}</span>
      </v-tooltip>
    </div>

    <!-- Settings menu -->
    <div class="mr-1">
      <v-menu
        v-model="showingSettingsMenu"
        :close-on-content-click="false"
        :width="230"
      >
        <template v-slot:activator="{ props: menuActivatorProps }">
          <v-tooltip location="bottom" :aria-label="$t('routes.recycleBin.settings')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="{ ...menuActivatorProps, ...props }"
                variant="text"
                :size="$vuetify.display.smAndDown ? 'small' : undefined"
                icon="mdi-folder-cog"
                :aria-label="$t('routes.recycleBin.settings')"
              />
            </template>
            <span>{{ $t('routes.recycleBin.settings') }}</span>
          </v-tooltip>

        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title>
              {{ $t('routes.recycleBin.showPreviews') }}
            </v-list-item-title>
            <template v-slot:append>
              <v-switch
                v-model="showThumbnails"
                density="compact"
                hide-details
                color="primary"
                class="visible-switch"
              />
            </template>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>

  <!-- Permanently delete all -->
  <RecycleBinConfirmationDialog
    v-if="$isTeamUser()"
    v-model="showingDeleteAllDialog"
    type="empty"
    :is-showing-dialog="showingConfirmationDialog"
    :error-message="confirmationDialogErrorMessage"
    :in-progress="deletionInProgress"
    @confirm="$emit('permanentlyDeleteAll')"
    @cancel="$emit('confirmationDialogCancel')"
  />

  <!-- Permanently delete selected -->
  <RecycleBinConfirmationDialog
    v-model="showingDeleteSelectedDialog"
    type="delete-selected"
    :is-showing-dialog="showingConfirmationDialog"
    :error-message="confirmationDialogErrorMessage"
    :in-progress="deletionInProgress"
    :isDisabled="!hasSelectedItems"
    @confirm="$emit('permanentlyDeleteSelected')"
    @cancel="$emit('confirmationDialogCancel')"
  />
</template>

<script>
import { useRecycleBinStore } from '@/stores/RecycleBinStore.js';
import _debounce from 'lodash/debounce';
import RecycleBinConfirmationDialog from '@/routes/recycle-bin/components/RecycleBinConfirmationDialog.vue';

export default {
  name: 'RecycleBinToolsBar',
  components: {
    RecycleBinConfirmationDialog,
  },
  props: {
    viewType: {
      type: String,
      required: true,
    },
    showingConfirmationDialog: {
      type: Boolean,
      default: false,
    },
    confirmationDialogErrorMessage: {
      type: String,
      default: '',
    },
    deletionInProgress: {
      type: Boolean,
      default: false,
    },
    showPreviews: {
      type: Boolean,
      default: true,
    },
    deletionTypeFilter: {
      type: String,
      default: 'all',
    },
  },
  emits: [
    'searchByFileName',
    'restoreSelected',
    'restoreAll',
    'permanentlyDeleteSelected',
    'permanentlyDeleteAll',
    'toggleView',
    'confirmationDialogShow',
    'confirmationDialogCancel',
    'thumbnailSettingChanged',
    'filterChanged',
  ],
  data() {
    return {
      showingRestoreMenu: false,
      showingPermanentlyDeleteMenu: false,
      showingSettingsMenu: false,
      showingFilterMenu: false,
      searchText: '',

      showingDeleteAllDialog: false,
      showingDeleteSelectedDialog: false,
      showThumbnails: this.showPreviews,
      selectedFilter: this.deletionTypeFilter,

      filterOptions: [
        {
          value: 'all',
          label: this.$t('routes.recycleBin.allFiles'),
        },
        {
          value: 'retention',
          label: this.$t('routes.recycleBin.deletedDueToRetentionPolicy'),
        },
      ],
    };
  },
  watch: {
    showThumbnails(newValue) {
      this.$emit('thumbnailSettingChanged', newValue);
    },
    deletionTypeFilter(newValue) {
      this.selectedFilter = newValue;
    },
  },
  methods: {
    searchByFileName: _debounce(function () {
      // TODO: Fine tune the minimum number of characters as well as the delay between emits.
      if (this.searchText && this.searchText.length >= 3) {
        this.$emit('searchByFileName', this.searchText);
      } else {
        this.$emit('searchByFileName', '');
      }
    }, 200),
    restoreAll() {
      if (this.hasAnyItems) {
        this.$emit('restoreAll');
      }
    },
    restoreSelected() {
      if (this.hasSelectedItems) {
        this.$emit('restoreSelected');
      }
    },
    applyFilters() {
      this.$emit('filterChanged', this.selectedFilter);
      this.showingFilterMenu = false;
    },
    clearFilters() {
      this.selectedFilter = 'all';
      this.$emit('filterChanged', this.selectedFilter);
    },
    openDeleteAllDialog() {
      this.showingDeleteAllDialog = true;

      this.showingPermanentlyDeleteMenu = false;
      this.$emit('confirmationDialogShow');
    },
    openDeleteSelectedDialog() {
      this.showingDeleteSelectedDialog = true;

      this.showingPermanentlyDeleteMenu = false;
      this.$emit('confirmationDialogShow');
    },
  },
  computed: {
    hasAnyItems() {
      const recycleBinStore = useRecycleBinStore();
      return recycleBinStore.getAllPods.length > 0;
    },
    hasSelectedItems() {
      const recycleBinStore = useRecycleBinStore();
      return recycleBinStore.getSelectedPods.length > 0;
    },
    hasActiveFilters() {
      return this.selectedFilter !== 'all';
    },
  },
};
</script>

<style scoped>
.recycle-bin-tools-bar {
  position: sticky;
  top: 0;
  padding: 12px 0 8px 0;
  z-index: 4;
  overflow: auto;
  background-color: rgb(var(--v-theme-background));
}

@media only screen and (max-width: 600px) {
  .recycle-bin-tools-bar {
    padding-top: 8px;
  }
}

.v-theme--dark .recycle-bin-tools-bar {
  background-color: #14171b;
}

.v-theme--light .recycle-bin-tools-bar {
  background-color: #e6ecf1;
}

.recycle-bin-tools-bar #search-box-input .v-field__input {
  background-color: white !important;
}

.enhanced-switch :deep(.v-switch__track) {
  background-color: rgba(0, 0, 0, 0.12) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
}

.v-theme--dark .enhanced-switch :deep(.v-switch__track) {
  background-color: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.enhanced-switch :deep(.v-switch__thumb) {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}

.v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.filter-card :deep(.v-field__input) {
  background-color: white !important;
}

.v-theme--dark .filter-card :deep(.v-field__input) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
